import React from 'react';
import styled from 'styled-components/macro';
import { Box, Tab, Tabs, Tooltip } from '@material-ui/core';
import { translations } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { RecurringItems } from './RecurringItems';
import { OneOffItems } from './OneOffItems';
import { DepositItems } from './DepositItems';
import { OtherReimbursementItems } from './OtherReimbursementItems';
import { commonStyle } from '../../../../styles/theme/themes';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAccessRight,
  selectContractStatus,
} from '../../WorkListPage/slice/selectors';
import { biStatus, biType } from '../../../../locales/types';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { useWorkListStateSlice } from '../../WorkListPage/slice';
import {
  selectOACLNummber,
  selectOneOff,
  selectRecurring,
} from '../slice/selectors';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { RecurringData } from '../slice/types';
import { apiList } from '../../../../locales/apiList';
import { UseAxios } from '../../../../utils/UseAxios';
import { TabWithTooltip } from 'app/components/TabWithTooltip';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

export function BillingItemsTab() {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(0);
  const { actions } = useWorkListStateSlice();
  const dispatch = useDispatch();
  const oacl = useSelector(selectOACLNummber);
  const contractStatus = useSelector(selectContractStatus);
  const recurringData: RecurringData | undefined = useSelector(selectRecurring);
  const oneOffData = useSelector(selectOneOff);
  const accessright = useSelector(selectAccessRight);
  const [recurringStatusIcon, setRecurringStatusIcon] =
    React.useState<JSX.Element>(<div />);
  const [oneOffStatusIcon, setOneOffStatusIcon] = React.useState<JSX.Element>(
    <div />,
  );
  const [depositStatusIcon, setDepositStatusIcon] = React.useState<JSX.Element>(
    <div />,
  );

  React.useEffect(() => {
    UseAxios(`${apiList.getWorklistByIDURL}${oacl}`, 'get')
      .then(function (response) {
        dispatch(actions.updateContractStatus(response.data));
      })
      .catch(function (error) {
        console.log('error: ' + JSON.stringify(error));
        dispatch(actions.updateErrorWordings(translations.error.commonError));
        dispatch(actions.updateErrorDialog(true));
      });
  }, []);

  React.useEffect(() => {
    if (!recurringData && !oneOffData) {
      setValue(2);
      dispatch(actions.updateSelectedPreviewSection(biType.DEPOSIT));
    } else if (!recurringData) {
      setValue(1);
      dispatch(actions.updateSelectedPreviewSection(biType.ONEOFF));

    }
  }, []);

  React.useEffect(() => {
    if (contractStatus) {
      updateStatusIcons(contractStatus);
    }
  }, [contractStatus]);

  function updateStatusIcons(statusData) {
    if (accessright.edit) {
      setRecurringStatusIcon(mapStatusToIcon(statusData.recurringStatus));
      setOneOffStatusIcon(mapStatusToIcon(statusData.oneOffStatus));
      setDepositStatusIcon(mapStatusToIcon(statusData.depositStatus));
    }
  }

  function mapStatusToIcon(status: biStatus) {
    switch (status) {
      case biStatus.PENDING:
        return <AssignmentLateOutlinedIcon style={{ fill: 'red' }} />;
      case biStatus.COMPLETE:
        return <AssignmentTurnedInOutlinedIcon />;
      case biStatus.TBC:
        return <PendingActionsIcon />;
      case biStatus.AUTOComplete:
        return <DoneOutlinedIcon />;
      default:
        return <div />;
    }
  }

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) {
      dispatch(actions.updateSelectedPreviewSection(biType.RECURRING));
    } else if (newValue === 1) {
      dispatch(actions.updateSelectedPreviewSection(biType.ONEOFF));
    } else if (newValue === 2) {
      dispatch(actions.updateSelectedPreviewSection(biType.DEPOSIT));
    } else if (newValue === 3) {
      dispatch(actions.updateSelectedPreviewSection(biType.OTHERREIMBURSEMENT));
    }
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <Wrapper>
      {contractStatus ? (
        <Box className="tab-container">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              className="tabs-container"
              value={value}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
            >
              {recurringData && (
                  <TabWithTooltip
                    tooltip={
                      (accessright.edit && contractStatus.recurringStatus === biStatus.AUTOComplete)
                        ? contractStatus?.statusReasons?.recurring
                        : ''
                    }
                    label={t(translations.biType.recurring)}
                    value={0}
                    {...a11yProps(0)}
                    icon={recurringStatusIcon}
                  />
              )}
              {oneOffData && (
                
                  <TabWithTooltip
                    tooltip={
                      (accessright.edit && contractStatus.oneOffStatus === biStatus.AUTOComplete)
                        ? contractStatus?.statusReasons?.oneOff
                        : ''
                    }
                    label={t(translations.biType.oneOff)}
                    value={1}
                    {...a11yProps(1)}
                    icon={oneOffStatusIcon}
                  />
              )}
              
                <TabWithTooltip
                  tooltip={
                    (accessright.edit && contractStatus.depositStatus === biStatus.AUTOComplete)
                      ? contractStatus?.statusReasons?.deposit
                      : ''
                  }
                  label={t(translations.biType.deposit)}
                  value={2}
                  {...a11yProps(2)}
                  icon={depositStatusIcon}
                />
              <Tab
                label={t(translations.contractDetail.otherReimburse)}
                value={3}
                {...a11yProps(3)}
              />
            </Tabs>
          </Box>
          {recurringData && (
            <TabPanel value={value} index={0}>
              <RecurringItems recurringData={recurringData} />
            </TabPanel>
          )}
          {oneOffData && (
            <TabPanel value={value} index={1}>
              <OneOffItems oneOffData={oneOffData} />
            </TabPanel>
          )}
          <TabPanel value={value} index={2}>
            <DepositItems />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <OtherReimbursementItems />
          </TabPanel>
        </Box>
      ) : (
        <LoadingIndicator />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  border-radius: 5px;
  margin-top: 10px;
  box-shadow: ${commonStyle.boxShadow};

  .MuiTab-root {
    border-width: thin;
  }

  .MuiTab-wrapper {
    display: initial;
  }

  .tab-container {
    .MuiBox-root {
      padding: 1px 5px 5px !important;
    }
  }

  .MuiTab-textColorPrimary.Mui-selected {
    background-color: ${p => p.theme.background};
  }
`;
